import { ActionTree } from 'vuex'
import { RootState } from '@/store/types'
import LoadingScreen from '@/modules/core/LoadingScreen'
import * as api from '@/modules/cms/api'
import { MutationTypes } from '@/modules/cms/store/mutations'
import { notify, Colors } from '@/modules/core/notification'
import { CMSState } from '@/modules/cms/store'
import { Page, PageTranslation } from '@/modules/cms/types'

const actions: ActionTree<CMSState, RootState> = {
    /**
     * Carga una página
     */
    loadPage: async ({commit, dispatch, rootGetters}, payload: {id: string}) => {
        if (payload.id === 'nueva') {
            await dispatch('buildNewPage')

            return
        }

        // tslint:disable-next-line:radix
        const page = await api.getPage(parseInt(payload.id))
        commit(MutationTypes.SetCurrentPage, page)
        // Por defecto se inserta la traducción en español
        commit(MutationTypes.SetCurrentTranslationByLocale, 'es')

        // Carga el análisis SEO de la página si no es interna
        if (!page.isInternal) {
            await dispatch('seo/loadSEOAnalysisReport', null, { root: true })
        }

        return page
    },

    /**
     * Carga la lista de páginas en el estado de la aplicación
     */
    loadPages: async ({state, commit}, payload = {showLoading: true}) => {
        // La lista de páginas ya ha sido obtenida
        if (state.pages.length) {
            return
        }

        commit(MutationTypes.SetLoadingPageList, true)
        const pages = await api.listPages()
        commit(MutationTypes.SetPages, pages)
        commit(MutationTypes.SetLoadingPageList, false)
    },

    /**
     * Sincroniza la lista de páginas
     */
    syncPages: async ({commit}) => {
        commit(MutationTypes.SetSyncPages, true)

        const pages = await api.syncPageRouteMap()
        commit(MutationTypes.SetPages, pages)

        // La sincronización ha terminado
        commit(MutationTypes.SetSyncPages, false)

        notify('La lista de páginas se ha sincronizado correctamente', Colors.Success)
    },

    /**
     * Guardar una página
     */
    savePage: async ({commit, state, dispatch}, payload = {isNew: false}) => {
        commit(MutationTypes.SetSavingPage, true)
        let page = state.currentPage

        try {
            page = payload.isNew ? await api.createPage(page) : await api.updatePage(page)

            commit(MutationTypes.SetCurrentPage, page)

            // Por defecto se inserta la traducción en español
            commit(MutationTypes.SetCurrentTranslationByLocale, 'es')
            commit(MutationTypes.SetSavingPage, false)

            notify('La página se ha guardado correctamente', Colors.Success)

            // Carga el análisis SEO de la página si no es interna
            if (!page.isInternal) {
                dispatch('seo/loadSEOAnalysisReport', null, { root: true })
            }
        } catch (e) {
            notify(`Error: ${e.message}`, Colors.Error)
            commit(MutationTypes.SetSavingPage, false)
        }
    },

    /**
     * Crea una nueva página y la inserta como página actualmente en edición
     */
    buildNewPage: async ({commit, state}) => {
        const page = new Page()
        page.isStatic = true

        const translation = new PageTranslation('es')
        page.translations.push(translation)

        commit(MutationTypes.SetCurrentPage, page)
        commit(MutationTypes.SetCurrentTranslationByLocale, 'es')
    },

    deletePage: async ({state, commit}) => {
        const page = state.pageToDelete
        await api.deletePage(page.id)

        notify(`La página ${page.id} se ha eliminado correctamente`)
        commit(MutationTypes.DeletePage, page.id)
        commit(MutationTypes.SetPageToDelete, new Page())
    },

    /**
     * Solicita al servidor que genere una pre-visualización de la página
     * actualmente en edición por el usuario
     *
     * @param commit
     */
    async loadPagePreview ({commit, state}) {
        commit(MutationTypes.TogglePagePreviewLoading, true)
        commit(MutationTypes.SetPagePreviewContent, await api.generatePagePreview(state.currentPage))
        commit(MutationTypes.TogglePagePreviewLoading, false)
    },
}

export default actions
